// src/ssg/seo.ts
// Issue #29：統一頁面 SEO head（title / description / canonical / og / JSON-LD）。
// 在 App.vue 呼叫一次，依 route.meta 自動產生；SSG 出爐的每頁 HTML 都含完整 label，
// runtime 路由切換時也即時更新（@vueuse/head 反應式）。
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useHead } from '@vueuse/head';

export const SITE = 'https://tcfc.org.tw';
export const SITE_NAME = 'TCFC 臺中市體育總會足球委員會';

/**
 * 正規化為 Cloudflare Pages 實際服務的 URL 形式（尾斜線）。
 * WHY：SSG 產出 /foo/index.html，乾淨網址 /foo 會 308 到 /foo/；
 *      canonical 與 JSON-LD 必須指向最終 URL，否則搜尋引擎會看到「宣告 A、實際 B」。
 */
export const normalizeUrl = (path: string): string =>
    path === '/' ? `${SITE}/` : `${SITE}${path.replace(/\/+$/, '')}/`;

const DEFAULT_DESCRIPTION =
    '臺中市體育總會足球委員會官方網站：市長盃足球錦標賽賽程、即時比分與臺中足球最新消息。';

// 2025 賽季尚未開賽（2025 頁面 = 賽事籌備中），2024 為已完賽
const TOURNAMENT_YEARS: Record<string, { zh: string; status: string }> = {
    '2024': { zh: '113年度', status: 'https://schema.org/EventCompleted' },
    '2025': { zh: '114年度', status: 'https://schema.org/EventScheduled' },
};

// 路由 meta.category → 中文組別名（僅比分頁有 category）
const DIVISION_NAMES: Record<string, string> = {
    open: '公開組',
    school: '學校組',
};

function buildJsonLd(path: string, meta: Record<string, unknown>) {
    const year = meta.year as string | undefined;
    const pathName = meta.title as string | undefined;

    if (path === '/') {
        return {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: SITE_NAME,
            url: SITE,
            description: DEFAULT_DESCRIPTION,
            email: 'service@tcfc.org.tw',
            contactPoint: { '@type': 'ContactPoint', email: 'service@tcfc.org.tw', contactType: 'customer service' },
        };
    }

    const y = year ? TOURNAMENT_YEARS[year] : undefined;
    // 分頁的組別（open/school）寫進 keywords：Google 對「賽事＋組別＋賽程」組合有實際索引效益
    const division = typeof meta.category === 'string' ? DIVISION_NAMES[meta.category] : undefined;
    return {
        '@context': 'https://schema.org',
        '@type': 'SportsEvent',
        name: pathName || `${year}年度市長盃足球錦標賽`,
        url: normalizeUrl(path),
        description: (meta.description as string) || DEFAULT_DESCRIPTION,
        eventStatus: y?.status || 'https://schema.org/EventScheduled',
        location: { '@type': 'Place', name: '臺中市' },
        keywords: ['臺中市長盃足球錦標賽', division, '賽程', '即時比分', '積分榜']
            .filter((k): k is string => Boolean(k))
            .join(','),
    };
}

/**
 * 頁面 SEO 統一入口：App.vue setup 呼叫一次即可。
 */
export function usePageSeo() {
    const route = useRoute();

    const title = computed(() =>
        route.meta.title ? `${route.meta.title} - TCFC` : SITE_NAME
    );
    const description = computed(() =>
        (route.meta.description as string) || DEFAULT_DESCRIPTION
    );
    const canonical = computed(() => normalizeUrl(route.path));
    const jsonLd = computed(() => buildJsonLd(route.path, route.meta as Record<string, unknown>));

    useHead({
        htmlAttrs: { lang: 'zh-TW' },
        title,
        meta: () => [
            { name: 'description', content: description.value },
            { property: 'og:title', content: title.value },
            { property: 'og:description', content: description.value },
            { property: 'og:type', content: 'website' },
            { property: 'og:url', content: canonical.value },
            { property: 'og:site_name', content: SITE_NAME },
            { property: 'og:locale', content: 'zh_TW' },
        ],
        link: () => [{ rel: 'canonical', href: canonical.value }],
        script: () => [
            // WHY 需 key：unhead 以 key 識別同一個 head 項目；無 key 時路由切換會「追加」而非「取代」，
            // 導致頁面殘留舊年度 JSON-LD（已實測：DOM 內同時有 Organization + SportsEvent）。
            { key: 'seo-jsonld', type: 'application/ld+json', innerHTML: JSON.stringify(jsonLd.value) },
        ],
    });
}